import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "../../components/feature/Header";
import Breadcrumbs from "../../components/feature/Breadcrumbs";
import Footer from "../../components/feature/Footer";
import WhatsAppFloating from "../../components/feature/WhatsAppFloating";
import CookieBanner from "../../components/feature/CookieBanner";
import QuoteFormSection from "../../pages/home/components/QuoteFormSection";
import SocialProofSection from "../../components/feature/SocialProofSection";
import SEO from "../../components/feature/SEO";
import FAQSection from "../../components/feature/FAQSection";

const services = [
  {
    title: "Reparación urgente",
    desc: "Tu persiana no sube o se ha roto. Llegamos en 24h y la arreglamos.",
    to: "/persianas/reparacion-persianas-malaga",
    icon: "ri-tools-line",
    rotation: "-rotate-[0.5deg]",
    linkText: "Reparar mi persiana hoy →",
  },
  {
    title: "Cambiar motor",
    desc: "Motor estropeado o lento. Cambiamos por nuevo con garantía 2 años.",
    to: "/persianas/motor-persianas",
    icon: "ri-flashlight-line",
    rotation: "rotate-[0.3deg]",
    linkText: "Cambiar motor de persiana →",
  },
  {
    title: "Persianas eléctricas",
    desc: "Instalación y reparación de persianas motorizadas. Mando a distancia incluido.",
    to: "/persianas/persianas-electricas",
    icon: "ri-remote-control-line",
    rotation: "-rotate-[0.3deg]",
    linkText: "Instalar persiana eléctrica →",
  },
  {
    title: "Cambiar cinta",
    desc: "Cinta rota, desgastada o atascada. La sustituimos en menos de una hora.",
    to: "/persianas/cambiar-cinta-persiana",
    icon: "ri-scissors-cut-line",
    rotation: "rotate-[0.5deg]",
    linkText: "Cambiar cinta de persiana →",
  },
  {
    title: "Persianas aluminio",
    desc: "Reparación e instalación de persianas de aluminio. Resistentes y duraderas.",
    to: "/persianas/persianas-aluminio",
    icon: "ri-home-gear-line",
    rotation: "-rotate-[0.5deg]",
    linkText: "Ver persianas de aluminio →",
  },
  {
    title: "Persianas PVC",
    desc: "Persianas de PVC económicas y eficientes. Ideal para hogares de la Costa del Sol.",
    to: "/persianas/persianas-pvc",
    icon: "ri-building-4-line",
    rotation: "rotate-[0.3deg]",
    linkText: "Ver persianas de PVC →",
  },
];

const pannes = [
  { label: "Mi persiana no sube", slug: "persiana-no-sube", icon: "ri-arrow-up-line" },
  { label: "Mi persiana no baja", slug: "no-baja", icon: "ri-arrow-down-line" },
  { label: "Mi persiana hace ruido", slug: "hace-ruido", icon: "ri-volume-up-line" },
  { label: "Se ha caído la cinta", slug: "caida-cinta", icon: "ri-scissors-cut-line" },
  { label: "El motor está estropeado", slug: "motor-estropeado", icon: "ri-flashlight-line" },
  { label: "Las lamas están rotas", slug: "lamas-rotas", icon: "ri-layout-masonry-line" },
];

const precios = [
  { servicio: "Cambiar cinta persiana", precio: "60 €" },
  { servicio: "Reparar motor persiana", precio: "80 €" },
  { servicio: "Sustituir motor", precio: "150 €" },
  { servicio: "Cambiar lamas (por lama)", precio: "15 €" },
  { servicio: "Revisión + presupuesto", precio: "GRATIS" },
];

const persianasFAQs = [
  {
    q: "¿Por qué mi persiana no sube?",
    a: "Lo más habitual es que la cinta esté rota o desenrollada del eje. También puede deberse a un motor quemado o a lamas atascadas en la guía. Nuestros técnicos en Málaga diagnostican el problema en minutos y reparan en la misma visita la mayoría de las veces. Si es el motor, llevamos repuestos para instalar uno nuevo el mismo día.",
  },
  {
    q: "¿Cuánto dura un motor de persiana?",
    a: "Un motor de persiana de calidad dura entre 10 y 15 años con un uso normal. En la Costa del Sol, la salinidad y el sol intenso aceleran el desgaste. Si notas ruidos, lentitud o olor a quemado, es señal de que necesita revisión antes de que falle por completo.",
  },
  {
    q: "¿Es mejor persiana de aluminio o PVC?",
    a: "El aluminio es más resistente a impactos y al sol intenso de Málaga, ideal para exteriores y ventanas grandes. El PVC es más económico, aísla mejor el frío y es más ligero. Para balcones expuestos al sol de la Costa del Sol, recomendamos aluminio. Para interiores, el PVC es una excelente opción.",
  },
  {
    q: "¿Se puede reparar una persiana sin cambiar el motor?",
    a: "En muchos casos sí. A veces basta con cambiar la cinta, ajustar el límite de carrera o sustituir el condensador. Solo recomendamos cambiar el motor cuando la bobina está quemada o el engranaje interno está roto, ya que la reparación interna no es rentable.",
  },
  {
    q: "¿Cuánto tiempo dura una reparación de persiana?",
    a: "La mayoría de reparaciones de persianas en Málaga se completan entre 30 y 60 minutos. Cambios de cinta, ajustes de motor o sustitución de lamas individuales son trabajos rápidos que hacemos en una sola visita. Solo una motorización completa o cambio de cajón puede llevar hasta una hora y media.",
  },
  {
    q: "¿Puedo motorizar cualquier persiana manual?",
    a: "Prácticamente sí. Las persianas manuales con eje de 40 o 60 mm admiten motor tubular sin problemas. El requisito principal es que el cajón tenga espacio suficiente para alojar el motor. En la visita gratuita por Málaga comprobamos las medidas y te confirmamos si tu persiana admite motorización segura.",
  },
];

export default function Persianas() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        ogType="website"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Persianas" }]}
        faqs={persianasFAQs}
      />
      <Header />
      <Breadcrumbs />
      <main>
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Reparación de persianas en Málaga por técnico propio Somfy Cherubini Becker"
          badge="⚡ 24h · Respuesta en 20 min · Málaga"
          title="¿Persiana atascada? La reparamos hoy mismo en Málaga"
          subtitle="Motor, cinta, lamas, persianas eléctricas. Diagnóstico gratuito en 24h en Málaga y Costa del Sol."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "#presupuesto", label: "Pedir presupuesto" }}
          trustSignals={["+10 años · desde 2015", "Seguro RC", "Garantía 2 años", "Atendemos toda la Costa del Sol"]}
        />

        <SocialProofSection />

        {/* SERVICIOS GRID */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Nuestros servicios de persianas</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  className={`bg-white rounded-[20px] p-8 ${s.rotation} border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">{s.desc}</p>
                  <Link to={s.to} className="inline-flex items-center gap-1 font-body font-semibold text-sm text-primary hover:text-primary-dark underline underline-offset-4 transition-colors">
                    {s.linkText}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ¿CUÁL ES TU PROBLEMA? */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">¿Cuál es tu problema?</h2>
            <p className="font-body text-lg text-text-secondary text-center mb-10 max-w-2xl mx-auto">Haz clic en tu avería y te diremos cómo la resolvemos.</p>
            <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
              {pannes.map((p) => (
                <Link
                  key={p.slug}
                  to={`/persianas/pannes/${p.slug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-light border border-border rounded-pill font-body font-semibold text-base text-text-primary hover:bg-primary hover:text-white hover:border-primary transition-all"
                >
                  <i className={`${p.icon} text-lg`}></i>
                  {p.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS ORIENTATIVOS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">Precios orientativos</h2>
            <p className="font-body text-lg text-text-secondary text-center mb-10 max-w-2xl mx-auto">
              Estos son precios aproximados. Cada persiana es diferente, por eso te damos un presupuesto exacto sin compromiso tras visitar tu hogar.
            </p>
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.servicio}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="font-body text-base md:text-lg text-text-primary">{p.servicio}</span>
                  <span className={`font-heading font-bold text-lg md:text-xl ${p.precio === "GRATIS" ? "text-primary" : "text-text-primary"}`}>
                    {p.precio}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos. Presupuesto exacto sin compromiso tras visita técnica.
            </p>
          </div>
        </section>

        {/* TRUST IMAGE */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <img
                src="https://storage.readdy-site.link/project_files/cf1847c3-949c-4dd2-b2c0-64bc50d47abe/fad0bd0b-95ab-4ad6-82d8-f88170431703_36ce386092c6e2bdc5920a95b209cb53.jpg?v=f84d6de2689e4604e3cb1e437ef0ea67"
                alt="Técnico profesional reparando persianas en Málaga"
                className="w-full h-auto rounded-xl object-cover aspect-[16/9]"
                loading="lazy"
                width="600"
                height="338"
              />
            </div>
          </div>
        </section>

        {/* TRUST BLOCK */}
        <section className="bg-text-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { emoji: "🏆", title: "+10 años · desde 2015", desc: "Reparando persianas en Málaga y la Costa del Sol desde 2015." },
                { emoji: "🔧", title: "Todas las marcas", desc: "Trabajamos con Somfy, Cherubini, Becker y cualquier marca del mercado." },
                { emoji: "🛡️", title: "Seguro RC", desc: "Seguro de Responsabilidad Civil para tu tranquilidad." },
                { emoji: "✅", title: "Garantía 2 años", desc: "En piezas y mano de obra. Si algo falla, volvemos sin coste." },
              ].map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-slate-800 rounded-[20px] p-8 border border-slate-700"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-3">{t.emoji}</div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">{t.title}</h3>
                  <p className="font-body text-sm text-slate-300 leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <FAQSection
          title="Preguntas frecuentes sobre reparación de persianas en Málaga"
          items={persianasFAQs}
        />

        {/* ===== SECCIÓN VIDEODIAGNÓSTICO — 2 COL: texto IZQ, imagen DER, fondo amarillo ===== */}
        <section className="py-16 md:py-24" style={{ backgroundColor: "#ECFCCB" }}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Texto */}
              <motion.div
                className="flex flex-col gap-6 md:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase" style={{ color: "#D97706" }}>
                  📹 NOVEDAD
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Diagnóstico GRATIS por videollamada WhatsApp
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  ¿No estás seguro del problema de tu persiana? Muéstranos por videollamada y te asesoramos <strong>GRATIS</strong>. Sin compromiso, sin desplazamiento, en menos de 20 minutos.
                </p>
                <ul className="flex flex-col gap-3">
                  {["Diagnóstico instantáneo", "Presupuesto orientativo al momento", "Ahorra la visita técnica si es sencillo", "Disponible L-V 9-19h, S 9-14h"].map((b) => (
                    <li key={b} className="flex items-center gap-3 font-body text-sm text-text-primary">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs" aria-hidden="true">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico%20de%20mi%20persiana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-xl px-8 py-5 rounded-pill transition-colors whitespace-nowrap w-fit"
                >
                  <span aria-hidden="true">📹</span> Iniciar videollamada
                </a>
              </motion.div>
              {/* COLUMNA DERECHA — Imagen */}
              <motion.div
                className="md:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=A%20real%20professional%20roller%20shutter%20repair%20technician%20showing%20a%20broken%20roller%20shutter%20mechanism%20to%20a%20client%20through%20a%20WhatsApp%20video%20call%20on%20a%20smartphone%20screen%2C%20Mediterranean%20home%20interior%20visible%20in%20the%20background%2C%20warm%20natural%20daylight%2C%20authentic%20professional%20service%20photography%2C%20detailed%20tools%20and%20hands%20visible%2C%20shallow%20depth%20of%20field&width=600&height=500&seq=persianas-hub-videocall&orientation=landscape&format=webp"
                  alt="Técnico de persianas en videollamada WhatsApp gratis para diagnóstico"
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}